import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, X, AlertTriangle, Clock, Shield } from "lucide-react";
import { doc, updateDoc, getFirestore } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CardForm = ({ onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const db = getFirestore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      const userRef = doc(db, "users", user.uid);
      const newPeriodEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      await updateDoc(userRef, {
        paymentMethodId: paymentMethod.id,
        paymentMethodLast4: paymentMethod.card.last4,
        paymentMethodBrand: paymentMethod.card.brand,
        'subscription.status': 'active',
        'subscription.currentPeriodEnd': newPeriodEnd,
        'subscription.updatedAt': new Date(),
        hasActiveSubscription: true,
        updatedAt: new Date().toISOString()
      });

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        '::placeholder': {
          color: '#9CA3AF',
        },
      },
      invalid: {
        color: '#EF4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-grey-700 mb-2">
          Card Details
        </label>
        <div className="border border-grey-300 rounded-lg p-4 bg-white focus-within:border-primary-500">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2.5 px-4 border border-grey-300 text-grey-700 font-medium rounded-lg hover:bg-grey-50 transition-colors"
        >
          Add Later
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              Add Payment Method
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const PaymentMethodReminder = ({ isOpen, onClose, isSubscriptionExpired }) => {
  const [showSuccess, setShowSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      onClose(true);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !isSubscriptionExpired && onClose(false)} />
      
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fadeIn">
        {!isSubscriptionExpired && (
          <button 
            onClick={() => onClose(false)}
            className="absolute top-4 right-4 text-grey-400 hover:text-grey-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {showSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-grey-900 mb-2">Payment Method Updated!</h3>
            <p className="text-grey-600">Your subscription is now active.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              {isSubscriptionExpired ? (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-grey-900 mb-2">Subscription Expired</h3>
                  <p className="text-grey-600">
                    Your subscription has expired. Add a payment method to continue using Pebble.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-grey-900 mb-2">Update Payment Method</h3>
                  <p className="text-grey-600">
                    Update your payment method to ensure uninterrupted access to Pebble.
                  </p>
                </>
              )}
            </div>

            {isSubscriptionExpired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  Your access to Pebble features is limited until you renew your subscription.
                </p>
              </div>
            )}

            <Elements stripe={stripePromise}>
              <CardForm onSuccess={handleSuccess} onClose={() => onClose(false)} />
            </Elements>

            <div className="flex items-center justify-center gap-2 mt-4 text-grey-500">
              <Shield className="w-4 h-4" />
              <span className="text-xs">Secure payment powered by Stripe</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodReminder;
